import { ACCOUNTING_API } from '../config/api';
import { accountingApi } from '../config/apiClient';

// DTO Interfaces
export interface PersonnelDto {
  Id: number;
  CompanyId: string;
  Name: string;
  Phone?: string;
  Email?: string;
  Position?: string;
  IsDeleted: boolean;
  CreatedAt: string;
  UpdatedAt: string;
}

export interface CashboxDto {
  Id: number;
  CompanyId: string;
  Name: string;
  Balance: number;
  Currency: string;
  IsDeleted: boolean;
  CreatedAt: string;
  UpdatedAt: string;
}

export interface AccountDto {
  Id: number;
  CompanyId: string;
  CompanyName: string;
  GroupId?: number;
  GroupFirebaseId?: string;
  AccountCode: string;
  AuthorizedPerson?: string;
  Phone?: string;
  MobilePhone?: string;
  Email?: string;
  Website?: string;
  PostalCode?: string;
  TaxOffice?: string;
  TaxNumber?: string;
  TradeRegisterNumber?: string;
  Country?: string;
  City?: string;
  District?: string;
  Address?: string;
  Notes?: string;
  Balance: number;
  DebitBalance: number;
  CreditBalance: number;
  BranchId?: string;
  FirebaseId?: string;
  IsActive: boolean;
  PreventNegativeBalance: boolean;
  RiskLimitAmount?: number;
  RiskLimitCurrency?: string;
  PaymentDueDays?: number;
  RiskLimitAction?: string;
  CreatedBy?: string;
  UpdatedBy?: string;
  CreatedAt: string;
  UpdatedAt: string;
}

export interface BankAccountDto {
  id?: number;
  Id?: number;
  companyId?: string;
  CompanyId?: string;
  bankName?: string;
  BankName?: string;
  accountNumber?: string;
  AccountNumber?: string;
  isPosAccount?: boolean;
  IsPosAccount?: boolean;
  isCheckAccount?: boolean;
  IsCheckAccount?: boolean;
  balance?: number;
  Balance?: number;
  currency?: string;
  Currency?: string;
  subAccounts?: SubAccountDto[]; // Array formatında
  SubAccounts?: SubAccountDto[]; // Array formatında
  isDeleted?: boolean;
  IsDeleted?: boolean;
  createdAt?: string;
  CreatedAt?: string;
  updatedAt?: string;
  UpdatedAt?: string;
}

export interface SubAccountDto {
  id?: number;
  Id?: number;
  accountName?: string;
  AccountName?: string;
  accountNumber?: string;
  AccountNumber?: string;
  iban?: string;
  Iban?: string;
  currency?: string;
  Currency?: string;
  balance?: number;
  Balance?: number;
  createdAt?: string;
  CreatedAt?: string;
  updatedAt?: string;
  UpdatedAt?: string;
  isActive?: boolean;
  IsActive?: boolean;
  parentId?: number;
  ParentId?: number;
  isPosAccount?: boolean;
  IsPosAccount?: boolean;
  isCheckAccount?: boolean;
  IsCheckAccount?: boolean;
}

export interface BankTransactionDto {
  Id: number;
  CompanyId: string;
  BankAccountId: number;
  BankAccountName?: string;
  BankSubAccountId?: number | null;
  BankSubAccountName?: string | null;
  AccountId?: number | null;
  AccountName?: string | null;
  Type: 'INCOME' | 'EXPENSE';
  TransactionType: string;
  Amount: number;
  Description?: string;
  ReferenceId?: string | null;
  ReferenceType?: string | null;
  Date: string;
  CreatedAt: string;
  UpdatedAt?: string | null;
  CreatedBy?: string | null;
  Currency: string;
  PaymentMethod?: string | null;
  BalanceBefore?: number;
  BalanceAfter?: number;
  Status?: string | null;
  ScreenId?: string | null;
  AuthCode?: string | null;
  BatchNo?: string | null;
  MaskedCardNumber?: string | null;
  FiscalTransactionId?: string | null;
  CardType?: string | null;
  DocumentNo?: string | null;
}

// Inventory DTOs
export interface InventoryWarehouseDto {
  Id: string;
  CompanyId: string;
  Name: string;
  Address?: string;
  Phone?: string;
  ResponsiblePerson?: string;
  Description?: string;
  IsDefault: boolean;
  IsActive: boolean;
  CreatedAt: string;
  ItemCount?: number;
}

export interface InventoryCategoryDto {
  Id: string;
  CompanyId: string;
  Name: string;
  ParentId?: string | null;
  ParentName?: string;
  Level: number;
  IsEcommerceEnabled: boolean;
  ImageUrl?: string;
  HeaderImageUrl?: string;
  OrderIndex?: number;
  Description?: string;
  IsActive: boolean;
  CreatedAt: string;
  ItemCount?: number;
}

export interface InventoryItemDto {
  Id: string;
  CompanyId: string;
  StockCode: string;
  Name: string;
  CategoryId?: string | null;
  CategoryName?: string;
  BrandId?: string | null;
  BrandName?: string;
  SalePrice: number;
  ListPrice?: number;
  PurchasePrice?: number;
  Unit?: string;
  Quantity?: number;
  Description?: string;
  Barcode?: string;
  ImageUrl?: string;
  IsActive: boolean;
  CreatedAt: string;
  UpdatedAt?: string;
  WarehouseStocks?: WarehouseStockDto[];
  TotalQuantity?: number;
}

export interface WarehouseStockDto {
  Id: string;
  ProductId: string;
  WarehouseId: string;
  WarehouseName?: string;
  Quantity: number;
  CreatedAt: string;
  UpdatedAt?: string;
}

// API Service
const accountingService = {
  // Personel işlemleri - ŞİMDİLİK ACCOUNT ENDPOINT'İNDEN ÇEKİYORUZ (İLERDE PERSONNEL ENDPOINT'İ EKLENEBİLİR)
  async getPersonnel(): Promise<PersonnelDto[]> {
    try {
      const { data: result } = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);
      const data = result.data || result;
      
      // Eğer veri array değilse boş array dön
      if (!Array.isArray(data)) {
        console.warn('Personel verisi array formatında değil:', data);
        return [];
      }
      
      return data;
    } catch (error) {
      console.error('Personel listesi alınamadı:', error);
      throw error;
    }
  },

  // Kasa işlemleri
  async getCashboxes(): Promise<CashboxDto[]> {
    try {
      const { data: result } = await accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY);
      const data = result.data || result;
      
      // Eğer veri array değilse boş array dön
      if (!Array.isArray(data)) {
        console.warn('Kasa verisi array formatında değil:', data);
        return [];
      }
      
      return data;
    } catch (error) {
      console.error('Kasa listesi alınamadı:', error);
      throw error;
    }
  },

  /**
   * Kasa oluşturur. Kasa modülündeki (Cashbox.tsx) kayıt payload'ı ile birebir aynıdır;
   * satış ekranı ayarlarından hızlı kasa açmak için kullanılır.
   */
  async createCashbox(input: {
    companyId: string;
    name: string;
    branchId?: string | null;
    description?: string;
    currency?: string;
    createdBy?: string;
  }): Promise<any> {
    const { data: result } = await accountingApi.post(ACCOUNTING_API.CASHBOXES, {
      companyId: input.companyId,
      name: input.name,
      description: input.description || '',
      currency: input.currency || 'TRY',
      branchId: input.branchId || null,
      isDefault: false,
      createdBy: input.createdBy || 'System',
    });
    return result?.data || result;
  },

  /**
   * Banka hesabı oluşturur. Banka modülündeki (AddBankAccount.tsx) kayıt payload'ı ile
   * aynıdır; satış ekranı ayarlarından hızlı POS hesabı açmak için kullanılır.
   */
  async createBankAccount(input: {
    companyId: string;
    bankName: string;
    accountNumber: string;
    branchId?: string | null;
    isPosAccount?: boolean;
    currency?: string;
    createdBy?: string;
  }): Promise<any> {
    const { data: result } = await accountingApi.post(ACCOUNTING_API.BANK_ACCOUNTS, {
      companyId: input.companyId,
      bankName: input.bankName,
      branchName: null,
      branchId: input.branchId || null,
      authorizedPerson: null,
      authorizedPhone: null,
      accountNumber: input.accountNumber,
      iban: null,
      currency: input.currency || 'TRY',
      isPosAccount: input.isPosAccount === true,
      isCheckAccount: false,
      createdBy: input.createdBy || 'System',
    });
    return result?.data || result;
  },

  // Cari hesap işlemleri
  /**
   * ID'ye göre cari hesap getir
   * ✅ MULTI-TENANT: companyId parametresi eklendi
   */
  async getAccountById(accountId: number): Promise<AccountDto | null> {
    try {
      const url = ACCOUNTING_API.ACCOUNT_BY_ID(accountId);
      const { data } = await accountingApi.get(url);
      return data;
    } catch (error: any) {
      console.error('Cari hesap getirilemedi:', error);
      return null;
    }
  },

  async getAccounts(): Promise<AccountDto[]> {
    try {
      const url = ACCOUNTING_API.ACCOUNT_BY_COMPANY;
      console.log(`[accountingService] 🌐 API Çağrısı: ${url}`);
      
      const { data: result } = await accountingApi.get(url);
      console.log(`[accountingService] 📦 API Response:`, result);
      
      const data = result.data || result;
      
      // Eğer veri array değilse boş array dön
      if (!Array.isArray(data)) {
        console.warn('[accountingService] ⚠️ Cari hesap verisi array formatında değil:', data);
        return [];
      }
      
      console.log(`[accountingService] ✅ ${data.length} cari hesap alındı`);
      return data;
    } catch (error) {
      console.error('[accountingService] ❌ Cari hesap listesi alınamadı:', error);
      throw error;
    }
  },

  async getGeneralAccount(): Promise<AccountDto | null> {
    try {
      console.log('[accountingService] 🔄 Genel Cari çekiliyor...');

      // Önce tam eşleşmeli uç: sayfalı aramadaki LIKE taraması ve hareket toplamı yok.
      // API henüz bu ucu bilmiyorsa (404) sayfalı aramaya düşülür.
      try {
        const { data: generalResult } = await accountingApi.get(ACCOUNTING_API.ACCOUNT_GENERAL);
        const generalAccount = generalResult?.data;
        if (generalAccount) {
          console.log('[accountingService] ✅ Genel Cari bulundu');
          return generalAccount;
        }
      } catch (generalError) {
        console.warn('[accountingService] ⚠️ Genel Cari ucu başarısız, sayfalı aramaya geçiliyor:', generalError);
      }

      // Sayfalı arama ile "Genel Cari" ara
      try {
        const pagedUrl = ACCOUNTING_API.ACCOUNT_BY_COMPANY_PAGED(1, 5, 'Genel Cari');
        const { data: pagedResult } = await accountingApi.get(pagedUrl);
        const pagedData = pagedResult.data || pagedResult;
        
        if (Array.isArray(pagedData) && pagedData.length > 0) {
          const generalAccount = pagedData.find((acc: any) =>
            acc.CompanyName === 'Genel Cari' || acc.companyName === 'Genel Cari'
          );
          if (generalAccount) {
            console.log('[accountingService] ✅ Genel Cari sayfalı aramayla bulundu');
            return generalAccount;
          }
        }
      } catch (pagedError) {
        console.warn('[accountingService] ⚠️ Sayfalı arama başarısız, tüm cariler çekilecek:', pagedError);
      }

      // Fallback: tüm carileri çek
      const allAccounts = await this.getAccounts();
      console.log(`[accountingService] 📊 Toplam ${allAccounts.length} cari bulundu (fallback)`);
      
      const generalAccount = allAccounts.find(acc => 
        acc.CompanyName === 'Genel Cari' || 
        (acc as any).companyName === 'Genel Cari'
      );
      
      if (generalAccount) {
        console.log('[accountingService] ✅ Genel Cari bulundu (fallback)');
      } else {
        console.warn('[accountingService] ⚠️ Genel Cari bulunamadı!');
        // İlk cariyi fallback olarak kullan
        if (allAccounts.length > 0) {
          console.warn('[accountingService] ⚠️ İlk cari "Genel Cari" olarak kullanılıyor:', allAccounts[0].CompanyName || (allAccounts[0] as any).companyName);
          return allAccounts[0];
        }
      }
      
      return generalAccount || null;
    } catch (error) {
      console.error('[accountingService] ❌ Genel Cari alınamadı:', error);
      throw error;
    }
  },

  // ✅ YENİ: Sayfalama ile cari getir (QuickSale modal için) - Backend sayfalama endpoint'ini kullan
  async getAccountsPaged(
    pageNumber: number = 1, 
    pageSize: number = 20,
    searchTerm: string = ''
  ): Promise<{ items: AccountDto[], totalCount: number, hasMore: boolean }> {
    try {
      console.log(`[accountingService] 🔄 Cariler çekiliyor (Sayfa: ${pageNumber}, Boyut: ${pageSize}, Arama: "${searchTerm}")`);
      
      // Backend sayfalama endpoint'ini kullan
      const url = ACCOUNTING_API.ACCOUNT_BY_COMPANY_PAGED(pageNumber, pageSize, searchTerm);
      console.log(`[accountingService] 🌐 Sayfalı API çağrısı: ${url}`);
      
      const { data: result } = await accountingApi.get(url);
      console.log(`[accountingService] 📦 Sayfalı API Response:`, result);
      
      // Backend'den dönen veri yapısını parse et
      const data = result.data || result;
      const pagination = result.pagination || result.Pagination || {};
      
      console.log(`[accountingService] 🔍 data type:`, Array.isArray(data) ? 'Array' : typeof data);
      console.log(`[accountingService] 🔍 pagination:`, pagination);
      
      // Backend yapısı: { success: true, data: [...], pagination: { totalCount, currentPage, pageSize, totalPages } }
      let accounts: any[] = [];
      let totalCount = 0;
      
      if (Array.isArray(data)) {
        // data direkt array ise
        accounts = data;
        totalCount = pagination.totalCount || pagination.TotalCount || 0;
      } else {
        // data obje ise (accounts property'si var)
        accounts = data.accounts || data.Accounts || [];
        totalCount = data.totalCount || data.TotalCount || 0;
      }
      
      console.log(`[accountingService] ✅ ${accounts.length} cari getirildi (Toplam: ${totalCount})`);
      
      const hasMore = (pageNumber * pageSize) < totalCount;
      
      return {
        items: accounts,
        totalCount,
        hasMore
      };
    } catch (error) {
      console.error('[accountingService] ❌ Sayfalı cari listesi alınamadı:', error);
      throw error;
    }
  },

  // Banka hesap işlemleri
  async getBankAccounts(): Promise<BankAccountDto[]> {
    try {
      const { data: result } = await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY);
      const data = result.data || result;
      
      // Eğer veri array değilse boş array dön
      if (!Array.isArray(data)) {
        console.warn('Banka hesap verisi array formatında değil:', data);
        return [];
      }
      
      return data;
    } catch (error) {
      console.error('Banka hesap listesi alınamadı:', error);
      throw error;
    }
  },

  // Banka hareketleri
  async getBankTransactionsByCompany(): Promise<BankTransactionDto[]> {
    try {
      console.log(`📋 [accountingService] Şirket banka hareketleri çekiliyor`);
      
      const { data: result } = await accountingApi.get(ACCOUNTING_API.BANK_TRANSACTIONS_BY_COMPANY);
      const data = result.data || result;
      
      // Eğer veri array değilse boş array dön
      if (!Array.isArray(data)) {
        console.warn('Banka hareket verisi array formatında değil:', data);
        return [];
      }
      
      console.log(`✅ [accountingService] ${data.length} banka hareketi çekildi`);
      return data;
    } catch (error) {
      console.error('❌ [accountingService] Banka hareketleri alınamadı:', error);
      throw error;
    }
  },

  // ========== INVENTORY İŞLEMLERİ ==========

  // Depo işlemleri
  async getWarehouses(includeItemCount: boolean = false): Promise<InventoryWarehouseDto[]> {
    try {
      const url = `${ACCOUNTING_API.INVENTORY_WAREHOUSES}${includeItemCount ? '?includeItemCount=true' : ''}`;
      const { data: result } = await accountingApi.get(url);
      const data = result.data || result;
      
      // Eğer veri array değilse boş array dön
      if (!Array.isArray(data)) {
        console.warn('Depo verisi array formatında değil:', data);
        return [];
      }
      
      return data;
    } catch (error) {
      console.error('Depo listesi alınamadı:', error);
      throw error;
    }
  },

  // Kategori işlemleri
  async getCategories(includeItemCount: boolean = false): Promise<InventoryCategoryDto[]> {
    try {
      const url = `${ACCOUNTING_API.INVENTORY_CATEGORIES}${includeItemCount ? '?includeItemCount=true' : ''}`;
      const { data: result } = await accountingApi.get(url);
      const data = result.data || result;
      
      // Eğer veri array değilse boş array dön
      if (!Array.isArray(data)) {
        console.warn('Kategori verisi array formatında değil:', data);
        return [];
      }
      
      return data;
    } catch (error) {
      console.error('Kategori listesi alınamadı:', error);
      throw error;
    }
  },

  // Ürün işlemleri
  async getInventoryItems(): Promise<InventoryItemDto[]> {
    try {
      const { data: result } = await accountingApi.get(ACCOUNTING_API.INVENTORY_ITEMS);
      const data = result.data || result;
      
      // Eğer veri array değilse boş array dön
      if (!Array.isArray(data)) {
        console.warn('Ürün verisi array formatında değil:', data);
        return [];
      }
      
      return data;
    } catch (error) {
      console.error('Ürün listesi alınamadı:', error);
      throw error;
    }
  }
};

export default accountingService;

